import { inject, Injectable, signal } from "@angular/core";

import { map, Observable, of, tap } from "rxjs";

import { DateTime } from "luxon";

import {
  HistoryRecord,
  PlanRecord,
  PlansResponse,
  PlanTableRecord,
  PlanTableResponse,
  RequestState,
  RequestStatus,
} from "@shared/models";
import { convertToHHMM, convertToMinutes, transformTimestampToLastUpdated } from "@shared/functions";
import { GlobalService, HistoryApiService, PlanApiService } from "@shared/services";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { CacheId } from "@shared/enums";
import { MessageService } from "primeng/api";

@Injectable({
  providedIn: "root",
})
export class PlanTableService {
  globalService = inject(GlobalService);
  planApiService = inject(PlanApiService);
  historyApiService = inject(HistoryApiService);
  messageService = inject(MessageService);
  tableHeight = signal<string>("");
  timestamp = signal<string>("");
  lastUpdatedText = signal<string>("");
  history: HistoryRecord[] = [];
  isLoading = signal<boolean>(false);

  constructor() {
    this.historyApiService
      .getHistory(false)
      .pipe(
        takeUntilDestroyed(),
        tap((response) => {
          if (response.response) {
            this.history = response.response.history;
          }
        })
      )
      .subscribe();
  }

  getPlanData(force: boolean, planKey: number): Observable<RequestState<PlanTableResponse>> {
    return this.planApiService.getPlan(force, planKey).pipe(
      map((response) => {
        if (response.isLoading && !response.response) {
          return new RequestState<PlanTableResponse>(RequestStatus.IsLoadingNoResponse);
        }
        return this.transformResponseToTableRecord(response);
      })
    );
  }
  updatePlanRecordsInDB(
    plan: PlanTableRecord[],
    uneditedPlan: PlanTableRecord[],
    cacheId: CacheId
  ): Observable<PlanRecord[] | null> {
    const editedRecords = plan.filter((record, index) =>
      (["vmax", "proj", "imax"] as (keyof PlanTableRecord)[]).some((key) => record[key] !== uneditedPlan[index][key])
    );

    const convertedEditedRecords = editedRecords.map(({ key, effectivePeriod, vmax, proj, imax }) => ({
      key,
      effectivePeriod,
      vmax: convertToMinutes(vmax),
      proj: convertToMinutes(proj),
      imax,
    }));
    this.isLoading.set(true);
    this.globalService.saving.set(true);
    return this.planApiService.updatePlanRecords(convertedEditedRecords).pipe(
      tap((response) => {
        if (response && response.length > 0) {
          this.updateLocalStoragePlan(response, cacheId);
          this.messageService.clear();
          this.messageService.add({
            severity: "success",
            summary: "Saved",
            detail: "Plan updated successfully.",
            key: "noReply",
          });
        }
      })
    );
  }

  private updateLocalStoragePlan(response: PlanRecord[], cacheId: CacheId): void {
    const localPlan = localStorage.getItem(cacheId);
    if (localPlan) {
      const parsedLocalPlan = JSON.parse(localPlan) as PlansResponse;
      parsedLocalPlan.plans = parsedLocalPlan.plans.map((plan) => {
        const editedRecord = response.find((record) => plan.effectivePeriod === record.effectivePeriod);
        if (editedRecord) {
          plan.vmax = editedRecord.vmax;
          plan.proj = editedRecord.proj;
          plan.imax = editedRecord.imax;
        }
        return plan;
      });
      localStorage.setItem(cacheId, JSON.stringify(parsedLocalPlan));
      this.isLoading.set(false);
      this.globalService.saving.set(false);
    }
  }

  private transformResponseToTableRecord(response: RequestState<PlansResponse>): RequestState<PlanTableResponse> {
    if (response.response) {
      const tableData = response.response.plans.map((plan) => {
        return {
          planID: plan.planID,
          key: plan.key,
          effectivePeriod: plan.effectivePeriod,
          vmax: convertToHHMM({ minutes: plan.vmax }),
          proj: convertToHHMM({ minutes: plan.proj }),
          imax: plan.imax,
          plannedPVDs: plan.plannedPVDs,
          plannedSick: plan.plannedSick,
          plannedTraining: plan.plannedTraining,
        };
      });
      const responseData = {
        plans: tableData,
        timestamp: response.response.timestamp,
      };
      if (response.isLoading) {
        return new RequestState<PlanTableResponse>(RequestStatus.IsLoadingWithCachedResponse, responseData);
      }
      this.handleTimestamp(response);
      return new RequestState<PlanTableResponse>(RequestStatus.IsCompleteWithResponse, responseData);
    }
    this.isLoading.set(false);
    return new RequestState<PlanTableResponse>(RequestStatus.HasErrorNoResponse);
  }

  setTableHeight(planKey: number): void {
    const difference = this.globalService.activePlanUnsaved() ? "245px" : "190px";
    const standbyPlanOffset = planKey === 1 ? "55px" : "0px";
    this.tableHeight.set(`calc(100vh - ${this.globalService.navbarHeight()}px - ${difference} - ${standbyPlanOffset})`);
  }

  recalculateImaxValues(plan: PlanTableResponse): Observable<RequestState<PlanTableResponse>> {
    plan.plans.forEach((record, index) => {
      record.imax = this.calculateImax(plan.plans, this.history.slice(0, -1), index);
    });

    return of(new RequestState<PlanTableResponse>(RequestStatus.IsCompleteWithResponse, plan));
  }

  private calculateImax(plan: PlanTableRecord[], history: HistoryRecord[], index: number): number {
    const RETROSPECTIVE_COUNT = 8;
    const SKIPPED_PROJ_VALUES = 2;
    let projValues: number[] = [];

    history.forEach((record) => {
      projValues.push(record.projMinutes);
    });

    plan.forEach((record) => {
      projValues.push(convertToMinutes(record.proj));
    });

    const RETROSPECTIVE_MINUTES = projValues
      .slice(SKIPPED_PROJ_VALUES + index, SKIPPED_PROJ_VALUES + index + RETROSPECTIVE_COUNT)
      .reduce((sum, value) => sum + value, 0);
    const BASE_VALUE_MINUTES = 64800;
    const PROSPECTIVE_MINUTES = 14760;
    return BASE_VALUE_MINUTES - RETROSPECTIVE_MINUTES - PROSPECTIVE_MINUTES;
  }

  private handleTimestamp(response: RequestState<PlansResponse>): void {
    if (response.response) {
      const timestamp = DateTime.fromISO(response.response.timestamp);
      this.timestamp.set(response.response.timestamp);
      this.lastUpdatedText.set(transformTimestampToLastUpdated(timestamp));
    }
  }
}
